/* eslint-disable consistent-return */
// File path: src/components/FileUploadWithProgress.tsx

import { XMarkIcon } from '@heroicons/react/24/outline';
import React, {
    useState,
    DragEvent,
    ChangeEvent,
    useRef,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from 'react';
import { useUploadFile } from 'api/hooks/useUploadFile';
import ImageModal from 'components/modal/image.modal';
import { base64ToBlob } from 'utilities/Base64toBlob';
import { UseMutateFunction } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNewUploadFile } from 'api/hooks/useNewUploadFile';
import { UploadIcon } from './icon';

export const fileTypes = {
    image: ['image/png', 'image/jpg', 'image/jpeg'],
    pdf: ['application/pdf'],
    cv: ['application/pdf', '.docx'],
    document: [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        '.pdf',
        '.docx',
        '.doc',
        '.xlsx',
        '.xls',
    ],
};

export interface FileUploaderRef {
    getIsPending: () => boolean;
}
interface FileUploadProps {
    isMutatePending?: boolean;
    title?: string;
    type?: 'Image' | 'PDF' | 'Document'; // Added Document type
    acceptFileTypes?: keyof typeof fileTypes;
    maxFileSizeMB?: number;
    initialFile?: string; // Changed from File to string (URL)
    onFileUploaded: (url: string) => void;
    showTitle?: boolean;
    onFileRemove?: () => void;
    haveCrop?: boolean;
    progress?: number;
    onMutateSuccess?: (res: AxiosResponse<any, any>) => void;
    onMutateFailed?: (err: Error) => void;
    useNewApi?: boolean;
    mutateFunc?: UseMutateFunction<
        AxiosResponse<any, any>,
        Error,
        File,
        unknown
    >;
}

const FileUploadWithProgress = forwardRef<FileUploaderRef, FileUploadProps>(
    (
        {
            title = 'Image',
            type = 'PDF',
            acceptFileTypes = 'pdf',
            maxFileSizeMB = 10,
            initialFile,
            onFileUploaded,
            showTitle = true,
            onFileRemove,
            haveCrop = false,
            mutateFunc,
            onMutateSuccess,
            onMutateFailed,
            progress,
            isMutatePending = false,
            useNewApi = false,
        },
        ref
    ) => {
        const { t } = useTranslation();
        const [dragging, setDragging] = useState(false);
        const fileInputRef = useRef<HTMLInputElement | null>(null);
        const [uploadProgress, setUploadProgress] = useState(progress);
        const [statusMessage, setStatusMessage] = useState<string | null>(null);
        const [previewUrl, setPreviewUrl] = useState<string | null>(
            initialFile || null
        );
        const [cropModalOpen, setIsCropModalOpen] = useState(false);
        const [imageToCrop, setImageToCrop] = useState('');
        const { mutate, isPending } = useUploadFile((p) => {
            setUploadProgress(Math.round((p.progress as number) * 100));
        });
        const { mutate: mutateNew, isPending: isPendingNew } = useNewUploadFile(
            (p) => {
                setUploadProgress(Math.round((p.progress as number) * 100));
            }
        );
        useImperativeHandle(ref, () => ({
            getIsPending: () => isPending || isPendingNew,
        }));
        useEffect(() => {
            if (initialFile) {
                setPreviewUrl(initialFile);
            }
        }, [initialFile]);

        useEffect(() => {
            if (progress) setUploadProgress(progress);
        }, [progress]);
        // Cleanup the object URL when component unmounts or file changes
        useEffect(() => {
            return () => {
                if (previewUrl && !initialFile) {
                    URL.revokeObjectURL(previewUrl);
                }
            };
        }, [previewUrl, initialFile]);

        const validateFile = (fileUploaded: File): boolean => {
            const allowedTypes = fileTypes[acceptFileTypes].map((ty) =>
                ty.trim()
            );
            const fileSizeMB = fileUploaded.size / (1024 * 1024);

            const isValidType = allowedTypes.some(
                (allowed) =>
                    fileUploaded.type === allowed ||
                    fileUploaded.name
                        .toLowerCase()
                        .endsWith(allowed.toLowerCase())
            );

            if (!isValidType) {
                alert(
                    `${t(
                        'Invalid file type. Allowed types:'
                    )} ${acceptFileTypes}`
                );
                return false;
            }

            if (fileSizeMB > maxFileSizeMB) {
                alert(`${t('File size exceeds')} ${maxFileSizeMB} MB.`);
                return false;
            }

            return true;
        };

        const handleFile = (selectedFile: File) => {
            if (selectedFile.type.startsWith('image/')) {
                const url = URL.createObjectURL(selectedFile);
                setPreviewUrl(url);
            } else {
                setPreviewUrl('pdf'); // For non-image files like PDF
            }
            setUploadProgress(0);
            setStatusMessage(null);
            if (mutateFunc) {
                mutateFunc(selectedFile, {
                    onSuccess: (res) => {
                        if (onMutateSuccess) onMutateSuccess(res);
                        setUploadProgress(100);
                        setStatusMessage(t('Upload successful!'));
                    },
                    onError: (err) => {
                        console.error(err);
                        if (onMutateFailed) onMutateFailed(err);
                        setUploadProgress(0);
                        setStatusMessage(t('Upload failed. Please try again.'));
                    },
                });
            } else if (useNewApi) {
                mutateNew(selectedFile, {
                    onSuccess: (res) => {
                        onFileUploaded(res.data.fileId);
                        setUploadProgress(100);
                        setStatusMessage(t('Upload successful!'));
                    },
                    onError: (err) => {
                        console.error(err);
                        setUploadProgress(0);
                        setStatusMessage(t('Upload failed. Please try again.'));
                    },
                });
            } else {
                mutate(selectedFile, {
                    onSuccess: (res) => {
                        onFileUploaded(res.data);
                        setUploadProgress(100);
                        setStatusMessage(t('Upload successful!'));
                    },
                    onError: (err) => {
                        console.error(err);
                        setUploadProgress(0);
                        setStatusMessage(t('Upload failed. Please try again.'));
                    },
                });
            }
        };

        const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setDragging(true);
        };

        const handleDrop = (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setDragging(false);
            const droppedFile = e.dataTransfer.files[0];
            if (droppedFile && validateFile(droppedFile)) {
                handleFile(droppedFile);
            }
            e.dataTransfer.clearData();
        };

        const handleDragLeave = () => {
            setDragging(false);
        };

        const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
            if (haveCrop) {
                const file = e.target.files?.[0];
                if (!file) return;

                const reader = new FileReader();
                reader.addEventListener('load', () => {
                    const imageUrl = reader.result?.toString() || '';
                    setImageToCrop(imageUrl); // Set the image first
                    setIsCropModalOpen(true);
                });

                reader.readAsDataURL(file);
            } else {
                const selectedFile = e.target.files?.[0];
                if (selectedFile && validateFile(selectedFile)) {
                    handleFile(selectedFile);
                }
            }
        };

        const handleClick = () => {
            fileInputRef.current?.click();
        };

        const handleRemoveFile = () => {
            if (onFileRemove) onFileRemove();
            setUploadProgress(0);
            setStatusMessage(null);
            setPreviewUrl(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        };

        const updateAvatar = (imgSrc: string) => {
            const blob = base64ToBlob(imgSrc, 'image/png');
            const file = new File([blob], 'cropped-image.png', {
                type: 'image/png',
            });
            handleFile(file);
        };

        /**
         * Automatically remove the status message after 3 seconds
         */
        useEffect(() => {
            if (statusMessage) {
                const timer = setTimeout(() => {
                    setStatusMessage(null);
                }, 3000); // 3000 milliseconds = 3 seconds

                // Cleanup the timer if the component unmounts or if statusMessage changes
                return () => clearTimeout(timer);
            }
        }, [statusMessage]);

        return (
            <div className="flex flex-col items-center justify-center w-full">
                {cropModalOpen && (
                    <ImageModal
                        modalOpen={cropModalOpen}
                        setModalOpen={setIsCropModalOpen}
                        updateAvatar={updateAvatar}
                        img={imageToCrop}
                    />
                )}
                <div
                    onClick={handleClick}
                    className={`w-full flex flex-col gap-4 items-center border-2 border-dashed border-border dark:border-border-dark rounded-2xl p-6 text-center transition-colors cursor-pointer relative ${
                        dragging
                            ? 'border-gray-800 bg-gray-50'
                            : 'border-border dark:border-border-dark '
                    }`}
                    onKeyUp={(e) => {
                        if (e.key === 'Enter') handleClick();
                    }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragLeave={handleDragLeave}
                    aria-label={`${t('File upload area for')} ${title}`}
                    role="button"
                    tabIndex={0}
                >
                    {previewUrl ? (
                        <div className="w-full h-full flex flex-col items-center justify-center relative">
                            {/* Image Preview */}
                            {type === 'Image' && (
                                <img
                                    src={previewUrl}
                                    alt={`Preview of ${title}`}
                                    className="max-w-full h-32 w-32 object-contain rounded"
                                />
                            )}
                            {/* PDF Preview */}
                            {type === 'PDF' && (
                                <div className="w-32 h-32 flex items-center justify-center bg-gray-100 rounded">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-16 w-16 text-red-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 4v16m8-8H4"
                                        />
                                        <path
                                            fillRule="evenodd"
                                            d="M6 6h12v12H6V6z"
                                            clipRule="evenodd"
                                            fill="currentColor"
                                        />
                                    </svg>
                                </div>
                            )}
                            {/* Document Preview */}
                            {type === 'Document' && (
                                <div className="w-32 h-32 flex items-center justify-center bg-gray-100 rounded">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-16 w-16 text-blue-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                    </svg>
                                </div>
                            )}

                            {/* Remove Button */}
                            <button
                                type="button"
                                className="absolute top-2 end-2 text-red-500 hover:text-red-700"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering the upload dialog
                                    handleRemoveFile();
                                }}
                                aria-label="Remove file"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>

                            {/* Upload Progress Overlay */}
                            {uploadProgress &&
                                uploadProgress > 0 &&
                                uploadProgress < 100 && (
                                    <div className="absolute inset-0 bg-black bg-opacity-25 flex items-center justify-center">
                                        <div className="w-32">
                                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                <div
                                                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                                                    style={{
                                                        width: `${uploadProgress}%`,
                                                    }}
                                                />
                                            </div>
                                            <div className="flex justify-end mt-1">
                                                <span className="text-xs text-white">
                                                    {uploadProgress}%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                            {/* Status Messages */}
                            {statusMessage && uploadProgress === 100 && (
                                <div className="absolute bottom-2 start-1/2 transform -translate-x-1/2 bg-green-500 text-white px-3 py-1 rounded-full text-sm">
                                    {statusMessage}
                                </div>
                            )}
                            {statusMessage && uploadProgress === 0 && (
                                <div className="absolute bottom-2 start-1/2 transform -translate-x-1/2 bg-red-500 text-white px-3 py-1 rounded-full text-sm">
                                    {statusMessage}
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <UploadIcon className="w-16 h-16 text-primary-content text-opacity-40" />
                            {showTitle && (
                                <p className="text-primary-content text-sm">
                                    {t('Drag and drop')} {title.toLowerCase()}{' '}
                                    {t('here')}, {t('or')}{' '}
                                    <span
                                        className="text-blue-500 underline cursor-pointer"
                                        role="button"
                                        tabIndex={0}
                                    >
                                        {t('browse')}
                                    </span>
                                </p>
                            )}
                            <ul className="flex flex-col items-start list-disc text-md text-secondary-content">
                                <li>
                                    {type === 'Document'
                                        ? t(
                                              'Accepted formats: PDF, Word, Excel'
                                          )
                                        : `${t(type)} ${t('format')}`}
                                </li>
                                <li>
                                    {t('Less than')} {maxFileSizeMB} MB
                                </li>
                            </ul>
                        </>
                    )}
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept={fileTypes[acceptFileTypes].join(', ')}
                        onChange={handleInputChange}
                    />
                </div>
            </div>
        );
    }
);

export default FileUploadWithProgress;
